import { v4 as uuid } from "uuid";

import {
  issue__EventType,
  Participant,
} from "@medbillai/generated-graphql-types";

import {
  GET_HELP_PROMPT_CASE_TYPE_MAP,
  type GetHelpPromptKey,
} from "@medbillai/schemas";

import type {
  BaseEvent,
  ChatEvent,
  ChatEventRow,
  DocumentUploadedDocumentRow,
  DocumentUploadedEvent,
  DocumentUploadedEventRow,
  EventSentByProfileRow,
  PreviewEventWithMetadata,
  UserSelectionRequestedEvent,
  UserSelectionRequestedEventRow,
} from "./types";

type EventOptions = {
  sentAt?: string;
};

const createSentByProfile = (participant: Participant) =>
  ({
    __typename: "user__Profile_Type",
    name: participant === Participant.USER ? "You" : null,
  }) satisfies EventSentByProfileRow;

type BaseEventFields = Pick<
  ChatEventRow,
  | "id"
  | "eventType"
  | "participant"
  | "sentAt"
  | "userVisible"
  | "eventFeedbacks"
  | "sentByProfile"
>;

const createBaseEvent = <T extends BaseEvent["__typename"]>({
  __typename,
  eventType,
  participant,
  sentAt,
}: {
  __typename: T;
  eventType: issue__EventType;
  participant: Participant;
  sentAt?: string;
}): BaseEventFields & { __typename: T } => ({
  __typename,
  id: uuid().toString(),
  eventType,
  participant,
  sentAt: sentAt ?? new Date().toISOString(),
  userVisible: true,
  eventFeedbacks: [],
  sentByProfile: createSentByProfile(participant),
});

export function createUserChatEvent(
  message: string,
  options: EventOptions = {},
): ChatEvent {
  return {
    ...createBaseEvent({
      __typename: "issue__ChatEvent_Type",
      eventType: issue__EventType.CHAT,
      participant: Participant.USER,
      sentAt: options.sentAt,
    }),
    chatBody: message,
  } satisfies ChatEventRow;
}

export function createSystemMessageEvent(
  message: string,
  options?: string[],
  eventOptions: EventOptions = {},
): UserSelectionRequestedEvent | ChatEvent {
  if (options && options.length > 0) {
    return {
      ...createBaseEvent({
        __typename: "issue__UserSelectionRequestedEvent_Type",
        eventType: issue__EventType.USER_SELECTION_REQUESTED,
        participant: Participant.SERVICE,
        sentAt: eventOptions.sentAt,
      }),
      stringMessage: message,
      stringOptions: options,
    } satisfies UserSelectionRequestedEventRow;
  }

  return {
    ...createBaseEvent({
      __typename: "issue__ChatEvent_Type",
      eventType: issue__EventType.CHAT,
      participant: Participant.SERVICE,
      sentAt: eventOptions.sentAt,
    }),
    chatBody: message,
  } satisfies ChatEventRow;
}

export function createDocumentUploadEvent(
  {
    id,
    documentId,
    uploading,
    sentAt,
    previewPromptMessage,
  }: {
    id?: string;
    documentId?: string;
    uploading?: boolean;
    sentAt?: string;
    // When set, the card renders this message (streamed) above the upload CTA —
    // used by the BILL_ISSUE_V2 preview so the prompt + action are one event.
    previewPromptMessage?: string;
  } = {
    uploading: false,
  },
): DocumentUploadedEvent {
  const row = {
    ...createBaseEvent({
      __typename: "issue__DocumentUploadedEvent_Type",
      eventType: issue__EventType.DOCUMENT_UPLOADED,
      participant: Participant.USER,
      sentAt,
    }),
    document: {
      __typename: "document__Document_Type" as const,
      id: documentId ?? uuid().toString(),
      name: "document.pdf",
      createdAt: new Date().toISOString(),
      files: [],
      dataType: null,
      latestIngestion: null,
    } satisfies DocumentUploadedDocumentRow,
  } satisfies DocumentUploadedEventRow;

  return {
    ...row,
    ...(id ? { id } : {}),
    ...(previewPromptMessage ? { previewPromptMessage } : {}),
    uploading,
    uploadFailed: false,
    isClusterEnd: false,
  };
}

export function createGetHelpPromptEvent(
  messageId: GetHelpPromptKey,
): PreviewEventWithMetadata {
  const prompt = GET_HELP_PROMPT_CASE_TYPE_MAP[messageId];
  const event = prompt.billUploadCard
    ? createDocumentUploadEvent({ previewPromptMessage: prompt.message })
    : createSystemMessageEvent(prompt.message, prompt.options);
  return { event, metadata: { messageId } };
}
